import axios from "axios";
import { useEffect, useState } from "react";
import {
  Eye,
  EyeSlash,
  Lock,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";

import SettingsCard from "./SettingsCard";
import SectionMessage from "./SectionMessage";

import {
  createEmptyMessage,
  getErrorMessage,
} from "../settingsUtils";

export default function PrivacyPinSettings({ token }) {
  const [status, setStatus] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  const [showPin, setShowPin] = useState(false);

  const [message, setMessage] = useState(createEmptyMessage);

  const clearMessage = () => {
    setMessage(createEmptyMessage());
  };

  const setError = (error) => {
    setMessage({
      error,
      success: "",
    });
  };

  const getStatus = async () => {
    if (!token) {
      return;
    }

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/privacy-pin/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStatus(response.data || {});
    } catch (error) {
      console.error(error);

      setError(
        getErrorMessage(
          error,
          "Failed to load privacy PIN settings."
        )
      );
    }
  };

  useEffect(() => {
    getStatus();
  }, [token]);

  const numbersOnly = (value) => {
    return value
      .replace(/[^0-9]/g, "")
      .slice(0, 4);
  };

  const resetForm = () => {
    setCurrentPin("");
    setNewPin("");
    setConfirmPin("");
    setShowPin(false);
  };

  const startEditing = () => {
    clearMessage();
    resetForm();
    setIsEditing(true);
  };

  const cancelEditing = () => {
    clearMessage();
    resetForm();
    setIsEditing(false);
  };

  const savePin = async () => {
    const enabled =
      status?.enabled ??
      status?.Enabled ??
      false;

    if (enabled && !/^\d{4}$/.test(currentPin)) {
      setError(
        "Please enter your current 4-digit PIN."
      );
      return;
    }

    if (!/^\d{4}$/.test(newPin)) {
      setError(
        "Please enter a new 4-digit PIN."
      );
      return;
    }

    if (newPin !== confirmPin) {
      setError(
        "The new PINs do not match."
      );
      return;
    }

    try {
      setIsSaving(true);
      clearMessage();

      await axios.post(
        `${ApiConnectionString}/api/privacy-pin/set`,
        {
          pin: newPin,
          currentPin: enabled
            ? currentPin
            : null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      resetForm();
      setIsEditing(false);

      setMessage({
        error: "",
        success: enabled
          ? "Privacy PIN changed successfully."
          : "Privacy PIN created successfully.",
      });

      await getStatus();
    } catch (error) {
      console.error(error);

      setError(
        getErrorMessage(
          error,
          "Failed to save your privacy PIN."
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const lockClientDetails = async () => {
    try {
      setIsSaving(true);
      clearMessage();

      await axios.post(
        `${ApiConnectionString}/api/privacy-pin/lock`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMessage({
        error: "",
        success: "Client details have been locked.",
      });

      await getStatus();
    } catch (error) {
      console.error(error);

      setError(
        getErrorMessage(
          error,
          "Failed to lock client details."
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const enabled =
    status?.enabled ??
    status?.Enabled ??
    false;

  const unlocked =
    status?.unlocked ??
    status?.Unlocked ??
    false;

  const lockedOut =
    status?.lockedOut ??
    status?.LockedOut ??
    false;

  return (
    <SettingsCard
      icon={<Lock />}
      title="Privacy PIN"
      description="Protect sensitive client details with your 4-digit PIN."
    >
      <div className="settings-form">
        <SectionMessage message={message} />

        <div>
          <label>
            Privacy PIN
          </label>

          <div className="settings-readonly-value">
            {enabled
              ? "Enabled"
              : "Not set"}
          </div>
        </div>

        <div>
          <label>
            Client details
          </label>

          <div className="settings-readonly-value">
            {unlocked
              ? "Unlocked"
              : "Locked"}
          </div>

          <small>
            When locked, client surnames are shortened and
            email addresses are hidden.
          </small>
        </div>

        {lockedOut && (
          <div className="alert alert-warning mb-0">
            Too many incorrect PIN attempts. Please try
            again later.
          </div>
        )}

        {isEditing && (
          <>
            {enabled && (
              <div>
                <label htmlFor="privacy-current-pin">
                  Current PIN
                </label>

                <input
                  id="privacy-current-pin"
                  type={showPin ? "text" : "password"}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  autoComplete="off"
                  value={currentPin}
                  onChange={(event) =>
                    setCurrentPin(
                      numbersOnly(event.target.value)
                    )
                  }
                  placeholder="••••"
                />
              </div>
            )}

            <div>
              <label htmlFor="privacy-new-pin">
                {enabled
                  ? "New PIN"
                  : "Create PIN"}
              </label>

              <input
                id="privacy-new-pin"
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoComplete="new-password"
                value={newPin}
                onChange={(event) =>
                  setNewPin(
                    numbersOnly(event.target.value)
                  )
                }
                placeholder="••••"
              />
            </div>

            <div>
              <label htmlFor="privacy-confirm-pin">
                Confirm new PIN
              </label>

              <input
                id="privacy-confirm-pin"
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoComplete="new-password"
                value={confirmPin}
                onChange={(event) =>
                  setConfirmPin(
                    numbersOnly(event.target.value)
                  )
                }
                placeholder="••••"
              />
            </div>

            <button
              type="button"
              className="settings-password-visibility"
              onClick={() =>
                setShowPin((current) => !current)
              }
            >
              {showPin ? (
                <>
                  <EyeSlash />
                  Hide PIN
                </>
              ) : (
                <>
                  <Eye />
                  Show PIN
                </>
              )}
            </button>
          </>
        )}

        <div className="settings-actions">
          {!isEditing ? (
            <>
              <button
                type="button"
                onClick={startEditing}
                disabled={lockedOut}
              >
                {enabled
                  ? "Change PIN"
                  : "Create PIN"}
              </button>

              {unlocked && (
                <button
                  type="button"
                  onClick={lockClientDetails}
                  disabled={isSaving}
                >
                  {isSaving
                    ? "Locking..."
                    : "Lock client details"}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={savePin}
                disabled={isSaving || lockedOut}
              >
                {isSaving
                  ? "Saving..."
                  : "Save PIN"}
              </button>

              <button
                type="button"
                onClick={cancelEditing}
                disabled={isSaving}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </SettingsCard>
  );
}